/**
 * Manage: one dialog per product (Revit or AutoCAD) that installs, updates, repairs, rolls
 * back and removes the plugin on many years at once, and the card button that opens it.
 *
 * The same dialog serves a first install from the catalog's ＋ and every later change from the
 * card, so there is one place to act on a product and one way to do it: tick the years
 * (all start ticked), pick the operation.
 *
 *   - `plan.ts`: which years to list and what each can do (pure, tested).
 *   - `PluginManageDialog.tsx`: the dialog: ticks, refresh, run, results.
 *   - `manage.css`: its styles.
 */
import { useMemo } from "react";
import { useLocale } from "../../../i18n/LocaleProvider";
import type { CadInstance } from "../../../bimgo";
import type { PluginsController } from "../../../hooks/usePlugins";
import type { PluginId } from "../../../lib/plugins";
import { manageItems, pendingCount } from "./plan";
import "./manage.css";

export { PluginManageDialog } from "./PluginManageDialog";
export {
  canRemove,
  canWrite,
  defaultSelection,
  manageItems,
  pendingCount,
  type ManageAction,
  type ManageItem,
} from "./plan";

/**
 * A card's only action. Plain when there is nothing to do; highlighted with a count when some
 * year is waiting for an install, update, repair or roll back, so the card says "act here"
 * without a second button beside it.
 */
export function ManageButton({ vendor, plugins, instances, onClick }: {
  vendor: PluginId;
  plugins: PluginsController;
  instances: readonly CadInstance[];
  onClick: () => void;
}) {
  const { t } = useLocale();
  const snapshots = plugins.state.kind === "loaded" ? plugins.state.snapshots : null;
  const pending = useMemo(() => pendingCount(manageItems(snapshots, instances, vendor)), [snapshots, instances, vendor]);
  const label = pending ? t("pluginManage.pending", { count: pending }) : t("plugins.card.manage");
  return (
    <button
      type="button"
      className={`settings-btn secondary manage-open${pending ? " has-pending" : ""}`}
      onClick={onClick}
      disabled={plugins.busy}
      title={label}
    >
      {t("plugins.card.manage")}
      {pending ? <span className="manage-count" aria-label={label}>{pending > 9 ? "9+" : pending}</span> : null}
    </button>
  );
}
